import React, { useState } from "react";
import StyledText from "../../components/StyledComponents/StyledText";
import StyledButton from "../../components/StyledComponents/StyledButton";
import UserIcon from "../../assets/svgIcons/UserIcon";
import StyledTable from "../../components/StyledComponents/StyledTable";
import TrashIcon from "../../assets/svgIcons/TrashIcon";
import StyledModal from "../../components/StyledComponents/StyledModal";
import PopupFileSvg from "../../assets/svgIcons/PopupFileSvg";
import styled from "styled-components";
import StyledInput from "../../components/StyledComponents/StyledInput";
import {
  identityTypes,
  media,
  validateEmail,
  validatePhone,
} from "../../utils/helper";
import CloseButtonIcon from "../../assets/svgIcons/CloseButtonIcon";
import AddIcon from "../../assets/svgIcons/AddIcon";
import RefreshIcon from "../../assets/svgIcons/RefreshIcon";
import {
  useAddIdentityMutation,
  useDeleteIdentityMutation,
} from "../../services/apis";
import { useUser } from "../../hooks/useAuth";
import { toast } from "react-toastify";

const ManageIdentities = ({ identities = [], refetch }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isDeleteItem, setIsDeleteItem] = useState(null);
  const [isVerificationModal, setVerificationModal] = useState(null);
  const [addIdentity, { isLoading: isAddIdentityLoading }] =
    useAddIdentityMutation();
  const [deleteIdentity, { isLoading: isDeleteIdentityLoading }] =
    useDeleteIdentityMutation();

  const { user } = useUser();
  const headers = [
    { label: "Identity", key: "value" },
    { label: "Assigning Date", key: "createdAt" },
  ];
  const [email, setEmail] = useState("");
  const actions = [
    {
      label: <TrashIcon />,
      onClick: (rowData) => setIsDeleteItem(rowData),
    },
  ];

  const onPendingVerification = (item) => {
    toast.success("Verification email send successfully");

    setVerificationModal(false);
  };

  const onPendingVerificationModal = (item) => {
    setVerificationModal(true);
  };
  const handleDeleteItem = async () => {
    if (!isDeleteItem) return;

    // TODO: data key is missing in the response
    const { error } = await deleteIdentity({
      identityId: isDeleteItem?.id,
    });

    if (error) {
      toast.error(
        error?.errorMessage ||
          error?.data?.errorMessage ||
          "Failed to delete identity"
      );
      return;
    }
    toast.success(`Identity "${isDeleteItem.value}" deleted`);

    refetch();
    setIsDeleteItem(null);
  };

  const handleAddButton = async () => {
    if (!email || (!validateEmail(email) && !validatePhone(email))) {
      toast.error("Please enter a valid email"); //or a phone number
      return;
    }

    const { data, error } = await addIdentity({
      userId: user?.userId || user?.id,
      type: validateEmail(email) ? identityTypes.email : identityTypes.phone,
      value: email,
    });

    if (data) {
      toast.success(`Identity "${email}" added`);

      refetch();
    } else if (error) {
      toast.error(
        error?.errorMessage ||
          error?.data?.errorMessage ||
          "Failed to add identity"
      );
    }

    setEmail("");
    setIsPopupOpen(false);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <StyledText $fontSize="1rem" $fontWeight="600">
          Manage Identities
        </StyledText>

        <StyledButton
          onClick={() => setIsPopupOpen(true)}
          _variant="primaryGradiant"
          className="d-none d-md-flex align-items-center px-3"
          icon={<UserIcon size="17px" />}
        >
          <StyledText $fontSize="14px" $fontWeight="600" color="white">
            Add Identity
          </StyledText>
        </StyledButton>

        <StyledButton
          onClick={() => setIsPopupOpen(true)}
          _variant="icon"
          className="d-flex d-md-none align-items-center"
          icon={<AddIcon />}
        />
      </div>

      {identities?.length > 0 && (
        <StyledTable
          cellAction={onPendingVerification}
          headers={headers}
          data={identities}
          actions={actions}
          callToFunc={{
            onPendingVerificationModal: onPendingVerificationModal,
          }}
        />
      )}

      <StyledModal show={isPopupOpen} onHide={() => setIsPopupOpen(false)}>
        <StyledModalBody className="d-flex align-items-center justify-content-center flex-column gap-2 px-4 px-md-0">
          <PopupFileSvg />
          <StyledText
            $fontSize="1rem"
            $fontWeight="600"
            className="my-3 my-md-4 text-center"
          >
            Enter email you want to add as identity
          </StyledText>

          <StyledInputContainer className="w-100 mb-4 mb-md-5">
            <StyledInput
              type="email"
              placeholder="Enter email address as Identity" /// phone number
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <StyledButton
              onClick={() => handleAddButton()}
              loading={isAddIdentityLoading}
            >
              <span className="d-none d-md-block">Add Identity</span>
              <span className="d-block d-md-none">
                <AddIcon />
              </span>
            </StyledButton>
          </StyledInputContainer>
        </StyledModalBody>
      </StyledModal>

      <StyledModal show={isDeleteItem} onHide={() => setIsDeleteItem(false)}>
        <StyledModalBody className="d-flex align-items-center justify-content-center flex-column gap-2">
          <PopupFileSvg />
          <StyledText
            $fontSize="1rem"
            $fontWeight="600"
            className="my-2 my-md-3"
          >
            Are you sure you want to delete this identity?
          </StyledText>
          <StyledText
            $fontSize="0.9rem"
            color="textColor"
            $fontWeight="400"
            className="mb-2 mb-md-4 text-center"
          >
            By deleting this identity, you will be no longer available to
            signing to user portal with this email address
          </StyledText>

          <div className="d-flex align-items-center justify-content-center gap-2 gap-md-3 pb-3 pb-md-5">
            <StyledButton
              onClick={() => setIsDeleteItem(false)}
              _variant="gray"
              weight="600"
              icon={<CloseButtonIcon size="21px" />}
            >
              Cancel
            </StyledButton>
            <StyledButton
              onClick={() => handleDeleteItem()}
              _variant="danger"
              weight="600"
              icon={<TrashIcon size="17px" />}
              loading={isDeleteIdentityLoading}
            >
              Delete
            </StyledButton>
          </div>
        </StyledModalBody>
      </StyledModal>

      <StyledModal
        show={isVerificationModal}
        onHide={() => setVerificationModal(false)}
      >
        <StyledModalBody className="d-flex align-items-center justify-content-center flex-column gap-2">
          <RefreshIcon />
          <StyledText
            $fontSize="1rem"
            $fontWeight="600"
            className="my-4 px-5 text-center"
          >
            Pending Verification Click the button below to resend verification
            email
          </StyledText>

          {/*<StyledButton*/}
          {/*  onClick={onPendingVerification}*/}
          {/*  _variant="danger"*/}
          {/*  weight="600"*/}
          {/*  icon={<TrashIcon size="17px" />}*/}
          {/*>*/}
          {/*  Delete*/}
          {/*</StyledButton>*/}
          <StyledButton
            onClick={onPendingVerification}
            _variant="primaryGradiant"
            className="d-flex align-items-center mt-3 px-5"
          >
            <StyledText $fontSize="0.9rem" $fontWeight="600" color="white">
              Send Email
            </StyledText>
          </StyledButton>
        </StyledModalBody>
      </StyledModal>
    </>
  );
};

const StyledInputContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 4px;
  overflow: hidden;
  display: flex;

  .input-main-container {
    margin-bottom: 0;
    flex-grow: 1;

    input {
      border: 0;
      padding: 12px;
    }
  }

  button {
    background: ${({ theme }) => theme.colors.btnColor};
    color: ${({ theme }) => theme.colors.secondary} !important;
    border-radius: 0;

    &:hover,
    &:active,
    &:focus {
      background: ${({ theme }) => theme.colors.borderColor} !important;
    }
  }
`;

const StyledModalBody = styled.div`
  max-width: 75%;
  ${media.xl`
       max-width : 100%;`}
`;
export default ManageIdentities;
